// WebSocket service for real-time updates
import { fetchAuthSession } from 'aws-amplify/auth';
import { eventEmitter, EVENTS } from '../events/EventEmitter';
import { TrackedObject } from '../types';
import objectsService from './ObjectsService';

export interface WebSocketMessage {
  type: string;
  payload: any;
}

class WebSocketService {
  private socket: WebSocket | null = null;
  private url: string;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private listeners: Map<string, Function[]> = new Map();
  private connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'failed' = 'disconnected';
  private messageQueue: WebSocketMessage[] = [];
  private processingQueue = false;
  
  constructor(url: string) {
    this.url = url;
  }
  
  async connect() {
    if (this.connectionStatus === 'connecting' || this.connectionStatus === 'connected') {
      console.log('WebSocket connection already established or in progress');
      return;
    }
    
    this.connectionStatus = 'connecting';
    
    try {
      let wsUrl = this.url;
      
      // In production, add authentication token
      if (process.env.NODE_ENV === 'production') {
        try {
          // Get current authenticated session
          const session = await fetchAuthSession();
          if (session.tokens?.idToken) {
            const token = session.tokens.idToken.toString();
            // Create WebSocket with authentication token
            wsUrl = `${this.url}?token=${token}`;
          }
        } catch (error) {
          console.error('Failed to get auth token for WebSocket:', error);
        }
      }
      
      console.log('WebSocket connecting to:', wsUrl);
      
      // Create WebSocket connection
      this.socket = new WebSocket(wsUrl);
      
      this.socket.onopen = this.handleOpen.bind(this);
      this.socket.onmessage = this.handleMessage.bind(this);
      this.socket.onclose = this.handleClose.bind(this);
      this.socket.onerror = this.handleError.bind(this);
      
      this.reconnectAttempts = 0;
      
      console.log('Attempting to connect to WebSocket:', wsUrl);
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
      this.connectionStatus = 'failed';
      this.attemptReconnect();
    }
  }
  
  private handleOpen(event: Event) {
    console.log('WebSocket connected successfully');
    this.connectionStatus = 'connected';
    this.emit('connected', null);
    
    // Process any queued messages
    this.processQueue();
  }
  
  private handleMessage(event: MessageEvent) {
    try {
      const data = JSON.parse(event.data) as WebSocketMessage;
      console.log('WebSocket message received:', data);
      
      // Queue the message for priority processing
      this.messageQueue.push(data);
      this.processQueue();
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
    }
  }
  
  // Process queued messages in order
  private async processQueue() {
    // Don't process if already processing
    if (this.processingQueue) return;
    
    this.processingQueue = true;
    
    try {
      while (this.messageQueue.length > 0) {
        const data = this.messageQueue.shift()!;
        
        // Handle location update messages from WebSocket
        if (data.type === 'LOCATION_UPDATE' && data.payload) {
          const locationUpdate = data.payload;
          if (locationUpdate.objectId && locationUpdate.latitude && locationUpdate.longitude) {
            console.log('Processing location update message immediately');
            // Process immediately to avoid delays
            await this.processLocationUpdate(locationUpdate);
          }
        }
        
        // General event emission for other subscribers
        this.emit(data.type, data.payload);
      }
    } finally {
      this.processingQueue = false;
    }
  }
  
  // Process and emit location update immediately
  private async processLocationUpdate(locationUpdate: any) {
    try {
      console.log('Processing WebSocket location update:', locationUpdate);
      
      // Try to get object details, but don't wait too long
      let objectInfo: any = null;
      try {
        const result = await Promise.race([
          objectsService.getObjectById(locationUpdate.objectId),
          new Promise(resolve => setTimeout(() => resolve(null), 500)) // 500ms timeout
        ]);
        objectInfo = result || { name: `Object ${locationUpdate.objectId}`, description: '' };
      } catch (e) {
        console.log('Could not get object details, using basic info');
        objectInfo = { name: `Object ${locationUpdate.objectId}`, description: '' };
      }
      
      // Try to get any existing location history from current state
      let locationHistory: any[] = [];
      // We need to preserve any existing location history if available, but this is lightweight
      // and doesn't try to maintain state in the WebSocket service
      try {
        const currentObject = await objectsService.getObjectById(locationUpdate.objectId);
        if (currentObject) {
          // Here we're using a convention where we might store location history
          // on the object in state somewhere else, but not expecting it from the server
          const existingObject = await objectsService.getObjectById(locationUpdate.objectId);
          if (existingObject && (existingObject as any).locationHistory) {
            locationHistory = (existingObject as any).locationHistory;
          }
        }
      } catch (e) {
        console.log('Could not retrieve existing location history');
      }
      
      // Create a history point for the new location
      const newPoint = {
        latitude: locationUpdate.latitude,
        longitude: locationUpdate.longitude,
        timestamp: locationUpdate.timestamp || Date.now()
      };
      
      // Update history (limited to last 10 points)
      const updatedHistory = [...locationHistory, newPoint].slice(-10);
      
      // Get any existing custom icon URL if available
      const iconUrl = objectInfo && (objectInfo as any).iconUrl ? 
                     (objectInfo as any).iconUrl : 
                     locationUpdate.iconUrl || undefined; // Check both sources
      
      // Create an updated object with the latest location data
      const updatedObject: TrackedObject = {
        id: locationUpdate.objectId,
        name: typeof objectInfo.name === 'string' ? objectInfo.name : `Object ${locationUpdate.objectId}`,
        description: typeof objectInfo.description === 'string' ? objectInfo.description : '',
        lastUpdate: locationUpdate.timestamp || Date.now(),
        latitude: locationUpdate.latitude,
        longitude: locationUpdate.longitude,
        isActive: true,
        locationHistory: updatedHistory,
        iconUrl: iconUrl // Include custom icon URL if available
      };
      
      console.log('Emitting real-time location update event:', updatedObject);
      
      // Emit to the application's event system IMMEDIATELY
      eventEmitter.emit(EVENTS.LOCATION_UPDATED, updatedObject);
    } catch (error) {
      console.error('Error processing location update:', error);
      
      // Even if we failed to get object info, still emit with what we have
      // Include any icon URL from the location update if available
      const fallbackObject: TrackedObject = {
        id: locationUpdate.objectId,
        name: `Object ${locationUpdate.objectId}`,
        description: '',
        lastUpdate: locationUpdate.timestamp || Date.now(),
        latitude: locationUpdate.latitude,
        longitude: locationUpdate.longitude,
        isActive: true,
        locationHistory: [{
          latitude: locationUpdate.latitude,
          longitude: locationUpdate.longitude,
          timestamp: locationUpdate.timestamp || Date.now()
        }],
        iconUrl: locationUpdate.iconUrl // Include any icon URL if provided
      };
      
      eventEmitter.emit(EVENTS.LOCATION_UPDATED, fallbackObject);
    }
  }
  
  private handleClose(event: CloseEvent) {
    console.log('WebSocket disconnected:', event.code, event.reason);
    this.connectionStatus = 'disconnected';
    this.emit('disconnected', { code: event.code, reason: event.reason });
    this.attemptReconnect();
  }
  
  private handleError(event: Event) {
    console.error('WebSocket error:', event);
    this.connectionStatus = 'failed';
    this.emit('error', event);
  }
  
  private attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error(`Failed to reconnect after ${this.maxReconnectAttempts} attempts`);
      this.emit('reconnectFailed', null);
      return;
    }
    
    this.reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
    
    console.log(`Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`);
    setTimeout(() => this.connect(), delay);
  }
  
  disconnect() {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
      this.connectionStatus = 'disconnected';
    }
  }
  
  getStatus() {
    return this.connectionStatus;
  }
  
  on(event: string, callback: Function) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event)!.push(callback);
    return this; // Enable method chaining
  }
  
  off(event: string, callback: Function) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event)!;
      const index = callbacks.indexOf(callback);
      if (index !== -1) {
        callbacks.splice(index, 1);
      }
    }
    return this; // Enable method chaining
  }
  
  emit(event: string, data: any) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event)!.slice(); // Clone to avoid mutation issues
      for (const callback of callbacks) {
        try {
          callback(data);
        } catch (e) {
          console.error(`Error in WebSocket ${event} listener:`, e);
        }
      }
    }
  }
  
  send(type: string, payload: any) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      const message = JSON.stringify({ type, payload });
      console.log('Sending message:', message);
      this.socket.send(message);
    } else {
      console.error('Cannot send message: WebSocket not connected');
    }
  }
}

// Create a singleton instance with the WebSocket URL from environment variables
const webSocketService = new WebSocketService(
  process.env.REACT_APP_WEBSOCKET_URL || 'wss://bl4ouebmy8.execute-api.us-west-2.amazonaws.com/prod'
);

export default webSocketService;
